import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useAuth } from '../../lib/AuthContext.jsx';
import { useLive, useNowMinute } from '../../lib/hooks.js';
import WorkflowTracker from '../../components/WorkflowTracker.jsx';
import BookingChat from '../../components/BookingChat.jsx';
import {
  Avatar,
  Loading,
  Empty,
  StatusPill,
  Modal,
  StarInput,
  useToast,
  Field,
  KeyValue,
  LiveBadge,
  Stars,
} from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import { currency, formatDate, formatDayLabel, timeRange, STATUS_LABEL, todayISO, isSlotExpired } from '../../lib/format.js';

/** Announcements shown when the other party moves the booking forward. */
const LIVE_MESSAGE = {
  accepted: (b) => `${b.nurse_name} accepted your booking!`,
  declined: (b) => `${b.nurse_name} declined this request.`,
  cancelled: () => 'This booking was cancelled.',
  completed: () => 'The visit was marked completed.',
};

const RATING_WORD = ['', 'Poor', 'Below expectations', 'Fine', 'Good', 'Excellent'];

export default function BookingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [busy, setBusy] = useState('');
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState('');
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [slots, setSlots] = useState([]);
  const [newSlot, setNewSlot] = useState({ date: '', startTime: '', endTime: '' });
  const now = useNowMinute(); // keeps the reschedule slot list free of expired times

  const fetcher = useCallback(() => api.get(`/api/bookings/${id}`), [id]);

  // The booking is polled so a patient watching the page sees the nurse's
  // response land without refreshing — this is the live half of the workflow.
  const {
    data: booking,
    setData: setBooking,
    loading,
    error,
    stale,
  } = useLive(fetcher, {
    interval: 12000,
    deps: [id],
    onChange: (next, prev) => {
      if (next.status !== prev.status && LIVE_MESSAGE[next.status]) {
        toast.success(LIVE_MESSAGE[next.status](next));
        if (navigator.vibrate) navigator.vibrate(next.status === 'accepted' ? [40, 60, 40] : 30);
      }
    },
  });

  const act = async (path, body, successMessage) => {
    setBusy(path);
    try {
      const updated = await api.post(`/api/bookings/${id}/${path}`, body);
      setBooking(updated);
      setDialog(null);
      setReason('');
      toast.success(successMessage);
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy('');
    }
  };

  const openReschedule = async () => {
    setDialog('reschedule');
    try {
      const nurse = await api.get(`/api/nurses/${booking.nurse_id}`);
      setSlots(
        nurse.availability.filter(
          (s) =>
            s.date >= todayISO() &&
            !nurse.bookedSlots.some(
              (b) => b.date === s.date && b.start_time < s.end_time && s.start_time < b.end_time
            )
        )
      );
    } catch (err) {
      toast.error(err);
    }
  };

  // Reschedule choices with same-day expired windows removed; recomputed as the
  // clock ticks so an option can't be selected after its start time has passed.
  const rescheduleSlots = slots.filter((s) => !isSlotExpired(s.date, s.start_time, now));

  // Drop a picked slot if it expires before the patient confirms.
  useEffect(() => {
    if (newSlot.date && isSlotExpired(newSlot.date, newSlot.startTime, now)) {
      setNewSlot({ date: '', startTime: '', endTime: '' });
    }
  }, [now, newSlot.date, newSlot.startTime]);

  if (error && !booking)
    return (
      <Empty art="offline" title="Booking unavailable">
        {error.message}
      </Empty>
    );
  if (loading && !booking) return <Loading rows={2} />;
  if (!booking) return null;

  const isPatient = user.role === 'patient';
  const isNurse = user.role === 'nurse';
  const other = isPatient
    ? { name: booking.nurse_name, phone: booking.nurse_phone, role: 'Your nurse', link: `/nurses/${booking.nurse_id}` }
    : { name: booking.patient_name, phone: booking.patient_phone, role: 'Patient' };

  const canCancel = ['pending', 'accepted'].includes(booking.status);
  const canReview = isPatient && booking.status === 'completed' && !booking.review_id;
  const noActions = !canCancel && !canReview && !(isNurse && ['pending', 'accepted'].includes(booking.status));

  const mapsHref =
    // A dropped pin is exact; the typed address is a best guess.
    booking.lat != null
      ? `https://maps.google.com/?q=${booking.lat},${booking.lng}`
      : `https://maps.google.com/?q=${encodeURIComponent(`${booking.address} ${booking.city || ''}`)}`;

  return (
    <>
      <button className="back-link" onClick={() => navigate(-1)}>
        <Icon name="arrow-left" />
        Back
      </button>

      <div className="page-head">
        <span className="icon-tile icon-tile-lg" aria-hidden="true">
          {booking.service_icon || <Icon name="stethoscope" />}
        </span>
        <div className="grow">
          <h1>{booking.service_name}</h1>
          <p>
            Booking #{booking.id} · {formatDayLabel(booking.date)}, {timeRange(booking.start_time, booking.end_time)}
          </p>
        </div>
        <div className="stack-sm" style={{ alignItems: 'flex-end' }}>
          <StatusPill status={booking.status} label={STATUS_LABEL[booking.status]} />
          {['pending', 'accepted'].includes(booking.status) && <LiveBadge stale={stale} />}
        </div>
      </div>

      <div className="mb-3">
        <WorkflowTracker booking={booking} role={user.role} />
      </div>

      <div className="detail-layout">
        <div className="stack">
          {/* Admins can open a booking for support, but the conversation stays
              between the patient and the nurse. */}
          {user.role !== 'admin' && <BookingChat booking={booking} />}

          <section className="card">
            <div className="card-header">
              <h2>Visit details</h2>
            </div>
            <div className="card-body">
              <dl style={{ margin: 0 }}>
                <KeyValue label="Date">
                  {formatDayLabel(booking.date)}, {formatDate(booking.date)}
                </KeyValue>
                <KeyValue label="Time">
                  {timeRange(booking.start_time, booking.end_time)} ({booking.hours} hr
                  {booking.hours === 1 ? '' : 's'})
                </KeyValue>
                <KeyValue label="Address">
                  {booking.address}
                  {booking.city ? `, ${booking.city}` : ''}
                  {booking.lat != null && (
                    <span className="tiny dim row-tight" style={{ justifyContent: 'flex-end', marginTop: 3 }}>
                      <Icon name="map-pin" size={12} />
                      Pinned · {Number(booking.lat).toFixed(5)}, {Number(booking.lng).toFixed(5)}
                    </span>
                  )}
                </KeyValue>
                {booking.notes && <KeyValue label="Notes">{booking.notes}</KeyValue>}
              </dl>
            </div>
            {booking.status === 'accepted' && (
              <div className="card-footer">
                <a className="btn btn-sm" href={mapsHref} target="_blank" rel="noreferrer">
                  <Icon name="map" /> Open in Maps
                </a>
                {other.phone && (
                  <a className="btn btn-sm" href={`tel:${other.phone}`}>
                    <Icon name="phone" /> Call {other.role.toLowerCase()}
                  </a>
                )}
              </div>
            )}
          </section>

          <section className="card">
            <div className="card-header">
              <h2 className="grow">Payment</h2>
              <StatusPill
                status={booking.payment_status}
                label={booking.payment_status === 'paid' ? 'Paid in cash' : 'Due in cash after the visit'}
              />
            </div>
            <div className="card-body">
              <dl style={{ margin: 0 }}>
                <KeyValue label="Rate">{currency(booking.nurse_rate)} / hour</KeyValue>
                <KeyValue label="Duration">{booking.hours} hours</KeyValue>
              </dl>
              <div className="summary-total">
                <span>Total</span>
                <span className="rate">{currency(booking.amount)}</span>
              </div>
            </div>
          </section>

          {booking.review_id && (
            <section className="card">
              <div className="card-header">
                <h2 className="grow">{isPatient ? 'Your review' : 'Patient review'}</h2>
                <Stars value={booking.review_rating} size={16} />
              </div>
              {booking.review_comment && (
                <div className="card-body">
                  <p className="muted">{booking.review_comment}</p>
                </div>
              )}
            </section>
          )}
        </div>

        <aside className="stack sticky-aside">
          <section className="card">
            <div className="card-header">
              <h3>{other.role}</h3>
            </div>
            <div className="card-body">
              <div className="row-tight">
                <Avatar name={other.name} />
                <div className="grow">
                  <div className="bold truncate">{other.name}</div>
                  {booking.status === 'accepted' && other.phone ? (
                    <a href={`tel:${other.phone}`} className="small row-tight" style={{ gap: 4 }}>
                      <Icon name="phone" size={13} />
                      {other.phone}
                    </a>
                  ) : (
                    <span className="tiny dim">
                      {booking.status === 'pending' ? 'Contact shared once confirmed' : ''}
                    </span>
                  )}
                </div>
              </div>
              {other.link && (
                <Link to={other.link} className="btn btn-sm btn-block mt-2">
                  View profile
                </Link>
              )}
            </div>
          </section>

          <section className="card">
            <div className="card-header">
              <h3>What can I do now?</h3>
            </div>
            <div className="card-body stack-sm">
              {isNurse && booking.status === 'pending' && (
                <>
                  <button
                    className="btn btn-success btn-block btn-lg"
                    disabled={busy === 'respond'}
                    onClick={() => act('respond', { action: 'accept' }, 'Booking accepted.')}
                  >
                    {busy === 'respond' ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <Icon name="check" /> Accept request
                      </>
                    )}
                  </button>
                  <button className="btn btn-danger btn-block" onClick={() => setDialog('decline')}>
                    <Icon name="close" /> Decline
                  </button>
                </>
              )}

              {isNurse && booking.status === 'accepted' && (
                <button
                  className="btn btn-primary btn-block"
                  disabled={busy === 'complete'}
                  onClick={() => act('complete', {}, 'Visit marked as completed.')}
                >
                  {busy === 'complete' ? (
                    <span className="spinner" />
                  ) : (
                    <>
                      <Icon name="flag" /> Mark visit completed
                    </>
                  )}
                </button>
              )}

              {canReview && (
                <button className="btn btn-primary btn-block btn-lg" onClick={() => setDialog('review')}>
                  <Icon name="star" /> Rate this visit
                </button>
              )}

              {isPatient && canCancel && (
                <button className="btn btn-block" onClick={openReschedule}>
                  <Icon name="calendar" /> Reschedule
                </button>
              )}

              {canCancel && (
                <button className="btn btn-danger btn-block" onClick={() => setDialog('cancel')}>
                  Cancel booking
                </button>
              )}

              {noActions && (
                <p className="tiny dim center">Nothing left to do — this booking is {booking.status}.</p>
              )}
            </div>
          </section>
        </aside>
      </div>

      {/* ------------------------------------------------------- Dialogs */}
      {dialog === 'cancel' && (
        <Modal
          title="Cancel this booking?"
          onClose={() => setDialog(null)}
          size={460}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setDialog(null)}>
                Keep booking
              </button>
              <button
                className="btn btn-danger"
                disabled={busy === 'cancel'}
                onClick={() => act('cancel', { reason }, 'Booking cancelled.')}
              >
                {busy === 'cancel' ? <span className="spinner" /> : 'Cancel booking'}
              </button>
            </>
          }
        >
          <p className="muted">The {isPatient ? 'nurse' : 'patient'} will be notified immediately.</p>
          <Field label="Reason" id="cancel-reason" optional>
            <textarea id="cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </Modal>
      )}

      {dialog === 'decline' && (
        <Modal
          title="Decline this request?"
          onClose={() => setDialog(null)}
          size={460}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setDialog(null)}>
                Go back
              </button>
              <button
                className="btn btn-danger"
                disabled={busy === 'respond'}
                onClick={() => act('respond', { action: 'decline', reason }, 'Request declined.')}
              >
                Decline request
              </button>
            </>
          }
        >
          <Field label="Reason for the patient" id="decline-reason" hint="A short explanation helps them rebook.">
            <textarea
              id="decline-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Already committed to another patient that morning."
            />
          </Field>
        </Modal>
      )}

      {dialog === 'review' && (
        <Modal
          title="Rate your visit"
          onClose={() => setDialog(null)}
          size={460}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setDialog(null)}>
                Later
              </button>
              <button
                className="btn btn-primary"
                disabled={busy === 'review'}
                onClick={() => act('review', { rating, comment }, 'Thanks for the feedback!')}
              >
                {busy === 'review' ? <span className="spinner" /> : 'Submit review'}
              </button>
            </>
          }
        >
          <p className="muted small center">How was your experience with {booking.nurse_name}?</p>
          <div className="center mb-3">
            <StarInput value={rating} onChange={setRating} />
            <p className="small bold mt-1" style={{ color: 'var(--accent)' }}>
              {RATING_WORD[rating]}
            </p>
          </div>
          <Field label="Comments" id="review-comment" optional>
            <textarea
              id="review-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Punctual, gentle with my mother, explained everything clearly…"
            />
          </Field>
        </Modal>
      )}

      {dialog === 'reschedule' && (
        <Modal
          title="Reschedule visit"
          onClose={() => setDialog(null)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setDialog(null)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={!newSlot.date || busy === 'reschedule'}
                onClick={() => act('reschedule', newSlot, 'Booking moved — awaiting confirmation.')}
              >
                Confirm new slot
              </button>
            </>
          }
        >
          <p className="muted small">
            Pick another window from {booking.nurse_name}'s calendar. The booking returns to “awaiting
            confirmation”.
          </p>
          {rescheduleSlots.length === 0 ? (
            <Empty art="schedule" title="No open slots">
              {slots.length > 0
                ? 'No available time slots remaining for today. Try again tomorrow.'
                : 'No other open slots available.'}
            </Empty>
          ) : (
            <div className="stack-sm">
              {Object.entries(
                rescheduleSlots.reduce((acc, s) => {
                  (acc[s.date] ||= []).push(s);
                  return acc;
                }, {})
              )
                .slice(0, 8)
                .map(([d, list]) => (
                  <div key={d}>
                    <div className="bold small mb-1">{formatDayLabel(d)}</div>
                    <div className="chips">
                      {list.map((s) => (
                        <button
                          key={s.id}
                          className="chip"
                          aria-pressed={newSlot.date === d && newSlot.startTime === s.start_time}
                          onClick={() => setNewSlot({ date: d, startTime: s.start_time, endTime: s.end_time })}
                        >
                          {timeRange(s.start_time, s.end_time)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
